import type { AggregationResponse } from '@cognite/sdk';

import { isChecklistItemStatus, isNotOkChecklistItemStatus } from '../utils';
import { valueAsInstanceId } from '../hooks/utils/type-utils';
import type { ChecklistItemStatusAggregationResponse } from '../types';

export type AggregationGroupItem = AggregationResponse['items'][number];

export type MapChecklistItemAggregateToStatusAggregationParams = {
  checklistExternalIds: string[];
  totalGroupItems: AggregationGroupItem[];
  statusGroupItems: AggregationGroupItem[];
};

const getCountFromGroupItem = (groupItem: AggregationGroupItem): number => {
  const aggregateField = groupItem.aggregates.find(
    (aggregate) => aggregate.aggregate === 'count'
  );

  // SDK aggregate entries are a union, only some include `value`, so we narrow it here.
  return aggregateField && 'value' in aggregateField
    ? (aggregateField.value ?? 0)
    : 0;
};

export const mapChecklistItemAggregateToStatusAggregation = ({
  checklistExternalIds,
  totalGroupItems,
  statusGroupItems,
}: MapChecklistItemAggregateToStatusAggregationParams): ChecklistItemStatusAggregationResponse => {
  const totalsByChecklist = new Map<string, number>();

  for (const groupItem of totalGroupItems) {
    const checklistId = valueAsInstanceId(
      groupItem.group?.checklist
    )?.externalId;
    const count = getCountFromGroupItem(groupItem);

    if (checklistId) {
      totalsByChecklist.set(checklistId, count);
    }
  }

  const completedByChecklist = new Map<string, number>();
  const notOkByChecklist = new Set<string>();

  for (const groupItem of statusGroupItems) {
    const checklistId = valueAsInstanceId(
      groupItem.group?.checklist
    )?.externalId;
    const status = groupItem.group?.status;
    const count = getCountFromGroupItem(groupItem);

    if (!checklistId || typeof status !== 'string') {
      continue;
    }

    if (isChecklistItemStatus(status)) {
      completedByChecklist.set(
        checklistId,
        (completedByChecklist.get(checklistId) ?? 0) + count
      );

      if (isNotOkChecklistItemStatus(status)) {
        notOkByChecklist.add(checklistId);
      }
    }
  }

  return checklistExternalIds.reduce<ChecklistItemStatusAggregationResponse>(
    (acc, checklistId) => {
      const total = totalsByChecklist.get(checklistId) ?? 0;
      const completed = completedByChecklist.get(checklistId) ?? 0;
      const notOk = notOkByChecklist.has(checklistId);

      acc[checklistId] = {
        total,
        completed,
        notOk,
        ok: completed === total && !notOk,
        isLoading: false,
      };

      return acc;
    },
    {}
  );
};
